import axios from 'axios';

// Use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const BRIDGE_API_URL = `${API_BASE_URL}/bridge`;

class BridgeApiService {
  /**
   * Create a new bridge transaction record after DepositForBurn
   */
  static async createTransaction(transactionData) {
    try {
      const response = await axios.post(`${BRIDGE_API_URL}/transaction`, {
        userAddress: transactionData.userAddress,
        sourceChain: transactionData.sourceChain,
        destinationChain: transactionData.destinationChain || 43114, // Default to Avalanche
        amount: transactionData.amount,
        burnTxHash: transactionData.burnTxHash,
        burnBlockNumber: transactionData.burnBlockNumber,
        sourceDomain: transactionData.sourceDomain,
        destinationDomain: transactionData.destinationDomain || 1, // Avalanche domain
        nonce: transactionData.nonce,
        isFastTransfer: transactionData.isFastTransfer || false,
      });

      return response.data;
    } catch (error) {
      console.error('Error creating bridge transaction:', error);
      throw error;
    }
  }

  /**
   * Get user's bridge transactions
   */
  static async getUserTransactions(userAddress, options = {}) {
    try {
      const params = new URLSearchParams({
        ...(options.status && { status: options.status }),
        ...(options.excludeStatus && { excludeStatus: options.excludeStatus }),
        ...(options.sourceChain && { sourceChain: options.sourceChain }),
        limit: options.limit || 4,
        offset: options.offset || 0,
      });

      const response = await axios.get(`${BRIDGE_API_URL}/transactions/${userAddress}?${params}`);

      return response.data;
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      throw error;
    }
  }

  /**
   * Get transaction by burn hash (always checks for updates)
   */
  static async getTransaction(burnTxHash) {
    try {
      const response = await axios.get(`${BRIDGE_API_URL}/transaction/${burnTxHash}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction:', error);
      throw error;
    }
  }

  /**
   * Check attestation status (will use cached data if available)
   */
  static async checkAttestation(burnTxHash) {
    try {
      const response = await axios.post(`${BRIDGE_API_URL}/attestation/check`, {
        burnTxHash,
      });

      return response.data;
    } catch (error) {
      console.error('Error checking attestation:', error);
      throw error;
    }
  }

  /**
   * Update transaction with burn confirmation details
   */
  static async updateBurnTransaction(burnTxHash, updateData) {
    try {
      const response = await axios.post(`${BRIDGE_API_URL}/transaction/burn-update`, {
        burnTxHash,
        ...updateData,
      });

      return response.data;
    } catch (error) {
      console.error('Error updating burn transaction:', error);
      throw error;
    }
  }

  /**
   * Update transaction with mint details
   */
  static async updateMintTransaction(burnTxHash, mintTxHash) {
    try {
      const response = await axios.post(`${BRIDGE_API_URL}/transaction/mint`, {
        burnTxHash,
        mintTxHash,
      });

      return response.data;
    } catch (error) {
      console.error('Error updating mint transaction:', error);
      throw error;
    }
  }

  /**
   * Mark transaction as completed
   */
  static async completeTransaction(burnTxHash, mintBlockNumber) {
    try {
      const response = await axios.post(`${BRIDGE_API_URL}/transaction/complete`, {
        burnTxHash,
        mintBlockNumber,
      });

      return response.data;
    } catch (error) {
      console.error('Error completing transaction:', error);
      throw error;
    }
  }

  /**
   * Get bridge statistics
   */
  static async getStatistics(userAddress = null) {
    try {
      const params = userAddress ? `?userAddress=${userAddress}` : '';
      const response = await axios.get(`${BRIDGE_API_URL}/stats${params}`);

      return response.data;
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }

  /**
   * Monitor transaction status using backend ethers provider
   */
  static async monitorTransaction(txHash, chainId) {
    try {
      const response = await axios.post(`${BRIDGE_API_URL}/monitor-transaction`, {
        txHash,
        chainId,
      });

      return response.data;
    } catch (error) {
      console.error('Error monitoring transaction:', error);
      throw error;
    }
  }

  /**
   * Get total bridged amount across all completed transactions
   */
  static async getTotalBridged() {
    try {
      const response = await axios.get(`${BRIDGE_API_URL}/total-bridged`);
      return response.data;
    } catch (error) {
      console.error('Error fetching total bridged amount:', error);
      return { success: false, data: { totalAmount: 0, totalTransactions: 0, displayAmount: '$0' } };
    }
  }

  /**
   * Get USDC balances for all chains for a user
   */
  static async getUserBalances(userAddress) {
    try {
      const response = await axios.get(`${BRIDGE_API_URL}/balances/${userAddress}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user balances:', error);
      return { success: false, data: {} };
    }
  }
}

export default BridgeApiService;
